/**
 * The rows behind one figure on the Pipeline.
 *
 * A tile is a claim about the board, and the first thing anybody wants from
 * a claim is the list it was counted from. "111 unowned" is a number to
 * worry about; the eleven rows that have been sitting there longest are
 * something to do.
 *
 * Each metric brings its own sentence saying what was counted and its own
 * sort, because what makes a row interesting is different every time: for
 * Unowned it is how long it has waited, for New this week it is how recently
 * it came in, for Stalled it is how long the silence has run.
 */
import { useApp } from '../../store/AppContext.jsx';
import { parseDate, daysAgo, formatDate } from '../../lib/dates.js';
import Icon from '../../lib/icons.jsx';
import Sheet from '../Sheet.jsx';
import { Avatar, StagePill, OwnerPill, EmptyState } from '../Primitives.jsx';
import PlanChip from '../PlanChip.jsx';
import {
  pipelineRows, isWorking, isStalled, companyName, leadDetails, lastTouch, clientOfDeal
} from '../../domain/crm.js';

const WEEK = 7 * 86400000;

/*
 * What each tile counts, what to say about it, and what to lead the list
 * with. `pick` runs against the board's working rows unless it says
 * otherwise; `over` is the whole history, wins included.
 */
const METRICS = {
  progress: {
    title: 'In progress', icon: 'card', tone: 'g-purple',
    note: 'Every request on the board that has not been won and has not been given up on.',
    pick: (S, crm, list) => list.filter(isWorking),
    sort: (S, crm) => (a, b) => (parseDate(b.created) || 0) - (parseDate(a.created) || 0),
    lead: 'age'
  },
  week: {
    title: 'New this week', icon: 'star', tone: 'g-blue',
    note: 'Requests that arrived in the last seven days. The warm end of the board.',
    pick: (S, crm, list) => list.filter(d => {
      const at = parseDate(d.created);
      return isWorking(d) && at && Date.now() - at.getTime() < WEEK;
    }),
    sort: () => (a, b) => (parseDate(b.created) || 0) - (parseDate(a.created) || 0),
    lead: 'age'
  },
  unowned: {
    title: 'Unowned', icon: 'users', tone: 'g-amber',
    note: 'Nobody has put their name to these. The oldest are first, because those are the ones the wait is costing.',
    pick: (S, crm, list) => list.filter(d => isWorking(d) && !d.owner),
    sort: () => (a, b) => (parseDate(a.created) || 0) - (parseDate(b.created) || 0),
    lead: 'age'
  },
  stalled: {
    title: 'Stalled', icon: 'alert', tone: 'g-red',
    note: 'Nothing has been logged against these inside the stall window — no call, no note, nothing.',
    pick: (S, crm, list) => list.filter(d => isStalled(S, crm, d)),
    sort: (S, crm) => (a, b) => (lastTouch(crm, a.id) || parseDate(a.created) || 0)
      - (lastTouch(crm, b.id) || parseDate(b.created) || 0),
    lead: 'quiet'
  },
  won: {
    title: 'Won', icon: 'check', tone: 'g-green',
    note: 'Reached Won and left for the Deals page. Almost all of these won themselves — somebody booked a demo and then registered on the site.',
    over: 'history',
    pick: (S, crm, list) => list.filter(d => d.stage === 'Won'),
    sort: () => (a, b) => (parseDate(b.close) || 0) - (parseDate(a.close) || 0),
    lead: 'closed'
  },
  rate: {
    title: 'Win rate', icon: 'chart', tone: 'g-cyan',
    note: 'Won against everything decided — won plus lost. A row nobody has given up on is not counted either way, which is why this reads 100% while most of the board has never been touched.',
    over: 'history',
    pick: (S, crm, list) => list.filter(d => d.stage === 'Won' || d.stage === 'Lost'),
    sort: () => (a, b) => (parseDate(b.close) || 0) - (parseDate(a.close) || 0),
    lead: 'closed'
  }
};

export default function PipelineStatView({ metric = 'progress', onClose }) {
  const { S, crm, rows, fmtDate, setSheet } = useApp();
  const m = METRICS[metric] || METRICS.progress;

  /* Everything that was ever on the board, wins included: Won and Win rate
     are questions about the history, not about what is left on it. */
  const history = pipelineRows(crm);
  const list = m.pick(S, crm, history).sort(m.sort(S, crm));

  /* The one number worth putting beside the count: what the list is really
     telling you, rather than how long it is. */
  const untouched = list.filter(d => !lastTouch(crm, d.id)).length;
  const decided = metric === 'rate'
    ? { won: list.filter(d => d.stage === 'Won').length, lost: list.filter(d => d.stage === 'Lost').length }
    : null;

  return (
    <Sheet
      icon={m.icon}
      tone={m.tone}
      title={m.title}
      onClose={onClose}
      closeLabel="Close"
      wide
      bodyClass="psv"
      hero={(
        <div className={`psvhero ${m.tone}`}>
          <div>
            <b>{decided ? `${decided.won + decided.lost ? Math.round(decided.won / (decided.won + decided.lost) * 100) : 0}%` : list.length}</b>
            <small>{m.note}</small>
          </div>
          <div className="psvchips">
            {decided ? (
              <>
                <span className="psvchip"><b>{decided.won}</b> won</span>
                <span className="psvchip"><b>{decided.lost}</b> lost</span>
                <span className="psvchip"><b>{history.filter(isWorking).length}</b> still undecided</span>
              </>
            ) : (
              <>
                <span className="psvchip"><b>{list.length}</b> rows</span>
                {untouched ? <span className="psvchip warn"><b>{untouched}</b> never touched</span> : null}
              </>
            )}
          </div>
        </div>
      )}
    >
      {!list.length ? (
        <div style={{ gridColumn: '1/-1' }}>
          <EmptyState icon={m.icon} tone={m.tone} title="Nothing here">
            Nothing on the board answers to this right now.
          </EmptyState>
        </div>
      ) : null}

      <div className="psvrows">
        {list.map(d => {
          const f = leadDetails(crm, d);
          const touch = lastTouch(crm, d.id);
          const came = parseDate(d.created);
          const closed = parseDate(d.close);
          const client = clientOfDeal(rows, d);
          const place = [f.city, f.country].filter(Boolean).join(', ');

          /* One figure on the right, and it is the one this list is about. */
          const stat = m.lead === 'closed'
            ? { n: closed ? formatDate(closed, false, S.dateFmt) : '—', l: 'closed' }
            : m.lead === 'quiet'
              ? { n: touch ? `${daysAgo(touch)}d` : (came ? `${daysAgo(came)}d` : '—'), l: touch ? 'since a note' : 'never touched' }
              : { n: came ? `${daysAgo(came)}d` : '—', l: 'on the board' };

          return (
            /* Not a <button>: the row carries the plan chip, which is one
               itself, and buttons cannot nest. See LeadRow in SalesOverview. */
            <div
              className="psvrow"
              key={d.id}
              role="button"
              tabIndex={0}
              onClick={() => setSheet({ kind: 'dealView', id: d.id })}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSheet({ kind: 'dealView', id: d.id });
                }
              }}
            >
              <Avatar name={d.title} />

              <div className="psvwho">
                <b>{d.title}</b>
                <small>
                  {f.email || f.phone || <span className="dim">no address on the row</span>}
                </small>
                <div className="psvmeta">
                  {companyName(crm, d.cid) ? (
                    <span><Icon name="building" />{companyName(crm, d.cid)}</span>
                  ) : null}
                  {place ? <span><Icon name="pin" />{place}</span> : null}
                  {d.source ? <span><Icon name="zap" />{d.source}</span> : null}
                  {f.landingPath ? <span><Icon name="link" />{f.landingPath}</span> : null}
                </div>
              </div>

              <div className="psvtags">
                <StagePill value={d.stage} />
                {client ? <span className="dealchip cli"><Icon name="check" />Client</span> : null}
                {!d.origin ? <span className="dealchip man"><Icon name="edit" />Manual</span> : null}
                <PlanChip list={f.productList} label={f.products} />
              </div>

              <div className="psvown">
                {d.owner ? <OwnerPill name={d.owner} /> : <span className="psvnone">Unowned</span>}
                <small>{f.requested ? `asked ${f.requested}` : came ? fmtDate(came) : ''}</small>
              </div>

              <div className="psvstat">
                <b>{stat.n}</b>
                <small>{stat.l}</small>
              </div>
            </div>
          );
        })}
      </div>

      {list.length ? (
        <p className="psvfoot">
          <Icon name="eye" />
          Open any row for the whole request — the queries, the campaign it came
          through and everything logged against it since.
        </p>
      ) : null}
    </Sheet>
  );
}
